import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { FunctionComponent } from "react";

import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { UserStorageKeys } from "@libs/gateways/user-experience/UserExperienceGateway.dtos.ts";
import { ReasonForVisitContext } from "@modules/clinical/screens/context/ReasonForVisitContext.ts";
import { ReasonForVisitHelper } from "@modules/clinical/screens/context/ReasonForVisitHelper.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { whenPermission } from "@ui-components/withPerm.tsx";

import { ReasonForVisitModal } from "../patient-record/components/clinical-form/ReasonForVisitModal.tsx";
import { QuickLinksBar } from "./components/QuickLinksBar.tsx";

const QuickLinksDataFetcher = () => {
  return (
    <DataFetcher
      fallback={null}
      fetch={async ({
        clinical,
        correspondence,
        document,
        practice,
        userExperience
      }) => {
        const [
          recordsIdsUserStorage,
          documentsIdsUserStorage,
          templateIdsUserStorage
        ] = await Promise.all([
          userExperience.getUserStorage(UserStorageKeys.OpenPatientTabs),
          userExperience.getUserStorage(UserStorageKeys.OpenDocuments),
          userExperience.getUserStorage(UserStorageKeys.OpenTemplates)
        ]);

        const persistedRecordTabs = recordsIdsUserStorage ?? [];
        const persistedDocumentTabs = documentsIdsUserStorage ?? [];
        const persistedTemplateTabs = templateIdsUserStorage ?? [];

        const contactIds = new Set<string>();

        persistedRecordTabs.forEach(tab => {
          if (tab.patientId) {
            contactIds.add(tab.patientId);
          }
        });

        persistedDocumentTabs.forEach(tab => {
          if (tab.patientId) {
            contactIds.add(tab.patientId);
          }
        });

        await practice.getContactsById(Array.from(contactIds));

        runInAction(() => {
          clinical.ui.openedRecordIds.replace(persistedRecordTabs);
          correspondence.openedDocumentTabs.replace(persistedDocumentTabs);
          document.openedTemplateTabs.replace(
            Array.from(persistedTemplateTabs)
          );
        });
      }}
    >
      {() => <QuickLinksBar />}
    </DataFetcher>
  );
};

const QuickLinksWithPermission = whenPermission(
  Permission.ClinicalRead,
  QuickLinksDataFetcher
);

export const QuickLinks: FunctionComponent = observer(() => {
  const root = useStores();

  return (
    <ReasonForVisitContext.Provider value={new ReasonForVisitHelper(root)}>
      <QuickLinksWithPermission />
      <ReasonForVisitModal
        title="Add a reason for consult to end consult"
        submitButtonProps={{
          text: "End consult",
          iconProps: undefined
        }}
      />
    </ReasonForVisitContext.Provider>
  );
});
